import QRCode from 'qrcode';

/**
 * Generate QR code data URL for an order
 * @param {string} orderId - The order ID to generate QR code for
 * @param {object} options - QR code generation options
 * @returns {Promise<string>} - Data URL of the generated QR code
 */
export const generateOrderQRCode = async (orderId, options = {}) => {
  try {
    const qrData = {
      orderId: orderId,
      type: 'order_verification',
      timestamp: Date.now(),
      // Add additional verification data
      verificationKey: `${orderId}_${Date.now()}`
    };

    const qrString = JSON.stringify(qrData);
    
    const defaultOptions = {
      errorCorrectionLevel: 'M',
      type: 'image/png',
      quality: 0.92,
      margin: 1,
      color: {
        dark: '#000000',
        light: '#FFFFFF'
      },
      width: 200
    };

    const finalOptions = { ...defaultOptions, ...options };
    
    const dataURL = await QRCode.toDataURL(qrString, finalOptions);
    return dataURL;
  } catch (error) {
    console.error('Error generating QR code:', error);
    throw new Error('Failed to generate QR code');
  }
};

/**
 * Generate QR code as SVG string for an order
 * @param {string} orderId - The order ID to generate QR code for
 * @param {object} options - QR code generation options
 * @returns {Promise<string>} - SVG string of the generated QR code
 */
export const generateOrderQRCodeSVG = async (orderId, options = {}) => {
  try {
    const qrData = {
      orderId: orderId,
      type: 'order_verification',
      timestamp: Date.now(),
      verificationKey: `${orderId}_${Date.now()}`
    };

    const qrString = JSON.stringify(qrData);
    
    const defaultOptions = {
      errorCorrectionLevel: 'M',
      margin: 1,
      color: {
        dark: '#000000',
        light: '#FFFFFF'
      },
      width: 200
    };

    const finalOptions = { ...defaultOptions, ...options };
    
    const svgString = await QRCode.toString(qrString, { type: 'svg', ...finalOptions });
    return svgString;
  } catch (error) {
    console.error('Error generating QR code SVG:', error);
    throw new Error('Failed to generate QR code SVG');
  }
};

/**
 * Parse QR code data to extract order information
 * @param {string} qrString - The QR code string data
 * @returns {object|null} - Parsed order data or null if invalid
 */
export const parseOrderQRCode = (qrString) => {
  try {
    const qrData = JSON.parse(qrString);
    
    // Validate QR code structure
    if (qrData.type !== 'order_verification' || !qrData.orderId) {
      throw new Error('Invalid QR code format');
    }
    
    return qrData;
  } catch (error) {
    console.error('Error parsing QR code:', error);
    return null;
  }
};

/**
 * Validate if QR code is for order verification
 * @param {string} qrString - The QR code string data
 * @returns {boolean} - True if valid order verification QR code
 */
export const isOrderVerificationQRCode = (qrString) => {
  const parsedData = parseOrderQRCode(qrString);
  return parsedData !== null && parsedData.type === 'order_verification';
}; 